// Plain calendar days as the API speaks them (YYYY-MM-DD). Pure and free of timezones, except where a day has to be read from "now".

const DAY_MS = 86_400_000;

const parse = (day: string): number => Date.parse(`${day}T00:00:00Z`);
const print = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Today in a timezone: the business's day, wherever the browser happens to be. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

export const addDays = (day: string, days: number): string => print(parse(day) + days * DAY_MS);

export const daysBetween = (from: string, to: string): number => Math.round((parse(to) - parse(from)) / DAY_MS);

/** 1 is Monday, 7 is Sunday. */
export function isoWeekday(day: string): number {
  const weekday = new Date(parse(day)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

export const mondayOf = (day: string): string => addDays(day, 1 - isoWeekday(day));

/** Monday of this week and the ones after it. */
export function nextMondays(today: string, count: number): string[] {
  const first = mondayOf(today);
  return Array.from({ length: count }, (_, index) => addDays(first, index * 7));
}

/** A month and the ones after it, as YYYY-MM. */
export function nextMonths(today: string, count: number): string[] {
  const [year, month] = today.split("-").map(Number) as [number, number];
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(Date.UTC(year, month - 1 + index, 1));
    return date.toISOString().slice(0, 7);
  });
}

const asDate = (day: string) => new Date(parse(day));

export function formatDayShort(day: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }).format(asDate(day));
}

export function formatWeekday(day: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }).format(asDate(day));
}

export function formatWeekdayName(isoDay: number, locale: string): string {
  // 2024-01-01 was a Monday.
  return new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(asDate(addDays("2024-01-01", isoDay - 1)));
}

export function formatMonth(month: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(asDate(`${month}-01`));
}

export function formatMonthName(monthIndex: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2024, monthIndex, 1)));
}

export function formatPeriod(from: string, to: string, locale: string): string {
  return `${formatDayShort(from, locale)} – ${formatDayShort(to, locale)}`;
}
